import React, { useState, useEffect } from "react";
import { Row, Col, Badge, Button } from "reactstrap";
import shallow from "zustand/shallow";
import useCurrentDealStore from "stores/DealStore";
import { useFromUser, useUser } from "stores/LocalStorageHelper";
import { getIdentityVerificationStatusByContact, createDealership } from "api/PlaidAPI";
import { saveFailAlert, saveSuccessAlert } from "utils/alertUtils";
import ChildrenModal from "components/modals/ChildrenModal";
import CoBuyerModal from "./CoBuyerModal";
import PlaidModal from "../buyer/PlaidModal";
import PlaidDetailsModal from "../buyer/PlaidDetailsModal";
import Credit from "assets/img/700partner.png";
import CheckMyInsuranceLogo from "assets/img/checkmydriver.png";

const CoBuyerHeader = () => {
	const [openPlaidModal, setOpenPlaidModal] = useState(false);
	const [openPlaidDetailsModal, setOpenPlaidDetailsModal] = useState(false);
	const [verificationStatus, setVerificationStatus] = useState(null);
	const [plaidError, setPlaidError] = useState(null);
	const [isSettingUpDealership, setIsSettingUpDealership] = useState(false);

	// Deal store
	const { coBuyer, coBuyerAddress, buyer } = useCurrentDealStore(
		(state) => ({
			coBuyer: state.coBuyer,
			coBuyerAddress: state.coBuyerAddress,
			buyer: state.buyer,
		}),
		shallow
	);

	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");

	// Get full user object from sessionStorage for dealership data
	const user = useUser();

	// Get usePlaid flag from user session (check both dealers and locations objects)
	const usePlaid = user?.dealers?.usePlaid || user?.locations?.usePlaid || false;

	const { ID, firstName, lastName } = coBuyer || "";

	const togglePlaid = () => {
		setOpenPlaidModal((prev) => !prev);
	};

	const togglePlaidDetails = () => {
		setOpenPlaidDetailsModal((prev) => !prev);
	};

	const handleSetupDealership = async () => {
		setIsSettingUpDealership(true);

		// Prevent any redirects by wrapping in try-catch at the top level
		try {
			// Safely get user data from sessionStorage without triggering redirects
			let userData = null;
			try {
				const userStr = sessionStorage.getItem("user");
				if (userStr) {
					userData = JSON.parse(userStr);
				}
			} catch (parseError) {
				console.error("Error parsing user data:", parseError);
			}

			if (!userData) {
				saveFailAlert("Unable to retrieve dealership information. Please refresh and try again.");
				setIsSettingUpDealership(false);
				return;
			}

			// Extract dealership data from user object
			const locations = userData?.locations || {};
			const dealers = userData?.dealers || {};

			console.log("RAW User Data:", { user: userData, locations, dealers });

			// Format the data as it will be sent to the API
			const dcDealerID = String(dealerID).padStart(4, "0") + String(locationID).padStart(4, "0");
			const formattedData = {
				dc_dealer_id: dcDealerID,
				is_multi_loc: false,
				status: "Active",
				name: dealers.companyName || "",
				address_line1: locations.address || "",
				address_line2: locations.unit || "",
				city: locations.city || "",
				state_province: locations.state || "",
				postal_code: locations.zipCode || "",
				country: locations.country || "US",
				primary_contact_name: locations.firstName && locations.lastName
					? `${locations.firstName} ${locations.lastName}`.trim()
					: "",
				primary_contact_email: locations.email || "",
				office_contact_phone: locations.phone || "",
				website_url: dealers.website || "",
			};

			console.log("FORMATTED API Payload:", formattedData);

			await createDealership({
				dealerID,
				locationID,
				dealerCompanyName: dealers.companyName || "",
				locationAddress: locations.address || "",
				locationUnit: locations.unit || "",
				locationCity: locations.city || "",
				locationState: locations.state || "",
				locationZipCode: locations.zipCode || "",
				locationFirstName: locations.firstName || "",
				locationLastName: locations.lastName || "",
				locationEmail: locations.email || "",
				locationPhone: locations.phone || "",
				dealerWebsite: dealers.website || "",
			});

			saveSuccessAlert("Dealership successfully setup for Plaid!");
			// Set verification status to unverified and clear the error
			setVerificationStatus("unverified");
			setPlaidError(null);

			// Store unverified status for both buyer and cobuyer in sessionStorage
			if (ID) {
				sessionStorage.setItem(`plaid_verification_${ID}`, JSON.stringify({ status: "unverified" }));
			}
			if (buyer?.ID) {
				sessionStorage.setItem(`plaid_verification_${buyer.ID}`, JSON.stringify({ status: "unverified" }));
			}
		} catch (error) {
			console.error("Error setting up dealership:", error);
			const errorMessage = error.response?.data?.error || error.message || "Failed to setup dealership for Plaid";
			saveFailAlert(errorMessage);
		} finally {
			setIsSettingUpDealership(false);
		}
	};

	// Check Plaid verification status when deal opens
	useEffect(() => {
		const checkVerificationStatus = async () => {
			if (ID && dealerID && locationID) {
				// If dealership doesn't have Plaid premium feature, always show "unverified"
				if (!usePlaid) {
					setVerificationStatus("unverified");
					return;
				}

				// Clear previous errors
				setPlaidError(null);

				// First, check sessionStorage
				const storageKey = `plaid_verification_${ID}`;
				const stored = sessionStorage.getItem(storageKey);

				if (stored) {
					try {
						const storedData = JSON.parse(stored);
						if (storedData.error) {
							setPlaidError(storedData.error);
						} else if (storedData.status) {
							setVerificationStatus(storedData.status);
						}
						return; // Use cached data, skip API call
					} catch (error) {
						console.error("Error parsing stored verification data:", error);
					}
				}

				// Only make API call if not in sessionStorage
				try {
					const dcDealerID = String(dealerID).padStart(4, "0") + String(locationID).padStart(4, "0");
					const response = await getIdentityVerificationStatusByContact(dcDealerID, String(ID));

					if (response.data && response.data.identity_verification_id) {
						const kycStatus = response.data.kyc_check?.status;
						const apiStatus = response.data.status;

						// Determine status
						let status;
						if (kycStatus === "success" && apiStatus === "success") {
							status = "verified";
						} else {
							status = "unverified";
						}

						setVerificationStatus(status);

						// Save to sessionStorage for future use
						const data = {
							verificationId: response.data.identity_verification_id,
							url: response.data.shareable_url,
							status: status,
						};
						sessionStorage.setItem(storageKey, JSON.stringify(data));
					} else {
						// No verification found - mark as unverified and cache it
						setVerificationStatus("unverified");
						sessionStorage.setItem(storageKey, JSON.stringify({ status: "unverified" }));
					}
				} catch (error) {
					// Handle different types of errors gracefully
					let errorMessage = null;

					// Log detailed error information
					console.error("Error checking verification status:", error);
					console.error("Error response data:", error.response?.data);
					console.error("Error response status:", error.response?.status);
					console.error("Error message:", error.message);

					if (error.response?.data?.error) {
						// Check for specific error messages from the API
						const apiError = error.response.data.error;
						console.error("API error message:", apiError);

						if (apiError.includes("Dealer not found") || apiError.includes("dc_dealer_id")) {
							errorMessage = "Dealership not setup with Plaid";
						} else if (apiError.includes("No verification found")) {
							// If no verification exists for this contact, mark as unverified and cache it
							setVerificationStatus("unverified");
							sessionStorage.setItem(storageKey, JSON.stringify({ status: "unverified" }));
							return;
						} else {
							errorMessage = "Plaid verification unavailable";
							console.error("Unexpected API error - full error object:", JSON.stringify(error.response?.data, null, 2));
						}
					} else {
						// Generic error
						errorMessage = "Plaid verification unavailable";
						console.error("Non-API error - full error object:", JSON.stringify({
							message: error.message,
							name: error.name,
							stack: error.stack
						}, null, 2));
					}

					if (errorMessage) {
						setPlaidError(errorMessage);
						// Cache the error so we don't keep hitting the API
						sessionStorage.setItem(storageKey, JSON.stringify({ error: errorMessage }));
					}
				}
			}
		};

		checkVerificationStatus();
	}, [ID, dealerID, locationID, usePlaid]);

	if (!ID) {
		return (
			<Row>
				<CoBuyerModal />
			</Row>
		);
	}

	return (
		<div>
			<Row>
				<div className="mx-auto w-100">
					<h4 className="text-center mb-3 mt-1">
						{firstName + " " + lastName}
					</h4>

					{/* Verification Status Badge */}
					{verificationStatus && (
						<Row className="justify-content-center mb-2">
							<Col xs="12" className="text-center">
								<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
									<Badge
										color={verificationStatus === "verified" ? "success" : "primary"}
										style={{
											cursor: verificationStatus === "verified" ? "default" : "pointer",
											fontSize: "0.95rem",
											padding: "0.45rem 0.9rem",
											marginBottom: 0
										}}
										onClick={verificationStatus === "verified" ? undefined : togglePlaid}
									>
										{verificationStatus === "verified" ? "Verified" : "Unverified"}
									</Badge>
									{verificationStatus === "verified" && (
										<Button
											color="info"
											size="sm"
											onClick={togglePlaidDetails}
											style={{
												fontSize: "0.95rem",
												padding: "0.45rem 0.9rem"
											}}
										>
											Details
										</Button>
									)}
								</div>
							</Col>
						</Row>
					)}

					{/* Plaid Error Message */}
					{plaidError && (
						<Row className="justify-content-center mb-2">
							<Col xs="12" className="text-center">
								{!plaidError.includes("Dealership not setup with Plaid") && (
									<small className="text-muted" style={{ fontSize: "0.85rem" }}>
										{plaidError}
									</small>
								)}
								{plaidError.includes("Dealership not setup with Plaid") && (
									<Button
										color="primary"
										size="sm"
										onClick={handleSetupDealership}
										disabled={isSettingUpDealership}
									>
										{isSettingUpDealership ? "Setting up..." : "Setup Plaid for Dealership"}
									</Button>
								)}
							</Col>
						</Row>
					)}

					{/* Action Buttons - Disabled */}
					<Row className="justify-content-center mb-2 mt-2">
						<Col xs="6" sm="6" className="text-center px-2">
							<div
								style={{
									cursor: "not-allowed",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									opacity: 0.5,
								}}
							>
								<img
									style={{
										borderRadius: "2px",
										width: "115px",
										height: "58px",
										objectFit: "contain"
									}}
									src={Credit}
									alt="700credit"
								/>
								<div className="d-flex align-items-center font-xs justify-content-center mt-1">
									Pull Credit <i className="nc-icon nc-minimal-right" />
								</div>
							</div>
						</Col>
						<Col xs="6" sm="6" className="text-center px-2">
							<div
								style={{
									cursor: "not-allowed",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									opacity: 0.5,
								}}
							>
								<img
									style={{
										borderRadius: "2px",
										width: "115px",
										height: "58px",
										objectFit: "contain"
									}}
									src={CheckMyInsuranceLogo}
									alt="https://modives.com/"
								/>
								<div className="d-flex align-items-center font-xs justify-content-center mt-1">
									Auto Insurance
									<i className="nc-icon nc-minimal-right" />
								</div>
							</div>
						</Col>
					</Row>

					<CoBuyerModal />
				</div>
			</Row>

			<ChildrenModal
				modalTitle="Plaid Verification"
				modal={openPlaidModal}
				toggle={togglePlaid}
				size="md"
			>
				<PlaidModal buyer={coBuyer} dealerID={dealerID} locationID={locationID} buyerAddress={coBuyerAddress} usePlaid={usePlaid} />
			</ChildrenModal>

			<PlaidDetailsModal
				isOpen={openPlaidDetailsModal}
				toggle={togglePlaidDetails}
				dcDealerID={String(dealerID).padStart(4, "0") + String(locationID).padStart(4, "0")}
				dcContactID={String(ID || "")}
				contactName={`${firstName || ""} ${lastName || ""}`.trim()}
				contactPhone={coBuyer?.cellPhone || coBuyer?.homePhone || coBuyer?.workPhone || ""}
			/>
		</div>
	);
};

export default CoBuyerHeader;
